import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, CheckCircle2, AlertTriangle, Trash2, Wrench } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DatePickerHR } from "../components/DatePickerHR";
import api, { fmtNum, fmtDate } from "../lib/api";

const TODAY = new Date().toISOString().slice(0, 10);

export default function ReconciliationPage() {
  const [banks, setBanks] = useState([]);
  const [bankId, setBankId] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [date, setDate] = useState(TODAY);
  const [stmt, setStmt] = useState("");
  const [result, setResult] = useState(null);
  const [diag, setDiag] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustNote, setAdjustNote] = useState("");

  const loadCheckpoints = () => api.get("/reconciliation/checkpoints").then((r) => setCheckpoints(r.data.checkpoints));

  useEffect(() => {
    api.get("/banks").then((r) => {
      setBanks(r.data);
      if (r.data[0]) setBankId(r.data[0].id);
    });
    loadCheckpoints();
  }, []);

  const check = async () => {
    const val = parseFloat(String(stmt).replace(",", "."));
    if (!bankId || !date || isNaN(val)) { toast.error("Odaberite banku, datum i upišite stanje s izvoda"); return; }
    setBusy(true);
    setDiag(null);
    try {
      const r = await api.get("/reconciliation/check", { params: { bank_id: bankId, currency, date, statement_balance: val } });
      setResult(r.data);
      if (!r.data.matched) {
        const from = r.data.suggested_from || `${date.slice(0, 7)}-01`;
        const d = await api.get("/reconciliation/diagnostics", { params: { bank_id: bankId, currency, date_from: from, date_to: date } });
        setDiag({ ...d.data, from });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri provjeri");
    } finally {
      setBusy(false);
    }
  };

  const saveCheckpoint = async () => {
    const val = parseFloat(String(stmt).replace(",", "."));
    setBusy(true);
    try {
      await api.post("/reconciliation/checkpoints", { bank_id: bankId, currency, date, statement_balance: val });
      toast.success("Kontrolna točka spremljena");
      loadCheckpoints();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally {
      setBusy(false);
    }
  };

  const adjust = async () => {
    setBusy(true);
    try {
      await api.post("/reconciliation/adjust", { bank_id: bankId, currency, date, amount: result.difference, note: adjustNote });
      toast.success("Korekcija salda knjižena");
      setAdjustOpen(false);
      setAdjustNote("");
      check();
      loadCheckpoints();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally {
      setBusy(false);
    }
  };

  const removeCheckpoint = async (id) => {
    await api.delete(`/reconciliation/checkpoints/${id}`);
    loadCheckpoints();
  };

  const bankName = banks.find((b) => b.id === bankId)?.short || "";

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2"><Scale className="w-6 h-6 text-[#0D9488]" /> Usklađivanje s izvodom</h1>
        <p className="text-sm text-[#475569] mt-1">Usporedite stanje s bankovnog izvoda sa stanjem u aplikaciji i pronađite uzrok razlike</p>
      </div>

      <Card className="rounded-sm border-[#CBD5E1]">
        <CardHeader className="pb-3"><CardTitle className="text-base">Provjera stanja</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Banka</Label>
            <Select value={bankId} onValueChange={setBankId}>
              <SelectTrigger data-testid="recon-bank-select" className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.short || b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Valuta</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="recon-currency-select" className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="EUR">EUR</SelectItem><SelectItem value="BAM">BAM</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex flex-col">
            <Label className="text-xs">Na datum</Label>
            <DatePickerHR data-testid="recon-date-input" value={date} onChange={(v) => v && setDate(v)} className="w-44" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Stanje na izvodu ({currency})</Label>
            <Input data-testid="recon-statement-input" className="w-40" placeholder="0,00" value={stmt} onChange={(e) => setStmt(e.target.value)} />
          </div>
          <Button data-testid="recon-check-btn" onClick={check} disabled={busy} className="bg-[#0D9488] hover:bg-[#0A3C64] rounded-sm">Provjeri</Button>
        </CardContent>
      </Card>

      {result && (
        <Card className={`rounded-sm border-2 ${result.matched ? "border-[#2F7D4F]/40 bg-[#2F7D4F]/5" : "border-[#DC2626]/40 bg-[#DC2626]/5"}`} data-testid="recon-result-card">
          <CardContent className="pt-5">
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <p className="text-xs text-[#475569]">Stanje na izvodu</p>
                <p className="text-lg font-bold tabular-nums">{fmtNum(result.statement_balance)} {currency}</p>
              </div>
              <div>
                <p className="text-xs text-[#475569]">Stanje u aplikaciji</p>
                <p className="text-lg font-bold tabular-nums" data-testid="recon-computed-balance">{fmtNum(result.computed_balance)} {currency}</p>
              </div>
              <div>
                <p className="text-xs text-[#475569]">Razlika</p>
                <p className={`text-lg font-bold tabular-nums ${result.matched ? "text-[#2F7D4F]" : "text-[#DC2626]"}`} data-testid="recon-difference">
                  {result.difference > 0 ? "+" : ""}{fmtNum(result.difference)} {currency}
                </p>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {result.matched ? (
                  <Badge className="bg-[#2F7D4F]/15 text-[#2F7D4F] hover:bg-[#2F7D4F]/15 rounded-sm gap-1" data-testid="recon-matched-badge"><CheckCircle2 className="w-3.5 h-3.5" /> Stanje se slaže</Badge>
                ) : (
                  <Badge className="bg-[#DC2626]/15 text-[#DC2626] hover:bg-[#DC2626]/15 rounded-sm gap-1" data-testid="recon-mismatch-badge"><AlertTriangle className="w-3.5 h-3.5" /> Razlika</Badge>
                )}
                <Button data-testid="recon-save-checkpoint-btn" variant="outline" size="sm" onClick={saveCheckpoint} disabled={busy} className="rounded-sm">Spremi kontrolnu točku</Button>
                {!result.matched && (
                  <Button data-testid="recon-adjust-open-btn" size="sm" onClick={() => setAdjustOpen(true)} className="bg-[#DC2626] hover:bg-[#8E4136] rounded-sm gap-1"><Wrench className="w-3.5 h-3.5" /> Korekcija salda</Button>
                )}
              </div>
            </div>
            {result.last_checkpoint ? (
              <p className="text-xs text-[#475569] mt-3">
                Zadnja usklađena kontrolna točka: <strong>{fmtDate(result.last_checkpoint.date)}</strong> ({fmtNum(result.last_checkpoint.statement_balance)} {currency}) — razlika je nastala nakon tog datuma.
              </p>
            ) : (
              !result.matched && <p className="text-xs text-[#475569] mt-3">Nema ranije usklađene kontrolne točke za {bankName} {currency} — dijagnostika prikazuje tekući mjesec. Kad uskladite stanje, spremite kontrolnu točku da sljedeći put tražite samo novije razdoblje.</p>
            )}
          </CardContent>
        </Card>
      )}

      {diag && (
        <Card className="rounded-sm border-[#CBD5E1]" data-testid="recon-diagnostics">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Dijagnostika: {fmtDate(diag.from)} – {fmtDate(date)} ({bankName} {currency})</CardTitle>
            <p className="text-xs text-[#475569]">Početno stanje {fmtNum(diag.start_balance)} → završno {fmtNum(diag.end_balance)} {currency}. Usporedite redak po redak s izvodom — gdje se saldo prestane slagati, tu je greška.</p>
            {diag.duplicate_groups > 0 && (
              <p className="text-xs font-medium text-[#DC2626]" data-testid="recon-duplicates-warning">⚠ Pronađeno {diag.duplicate_groups} grupa mogućih duplikata (označeni u tablici) — provjerite jesu li stavke uvezene dvaput.</p>
            )}
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Opis</TableHead>
                    <TableHead>Kategorija</TableHead>
                    <TableHead>Unio</TableHead>
                    <TableHead className="text-right">Iznos</TableHead>
                    <TableHead className="text-right">Saldo nakon</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diag.transactions.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#64748B] py-6">Nema ostvarenih transakcija u razdoblju — provjerite je li izvod za ovo razdoblje uopće uvezen ili je početno stanje banke krivo.</TableCell></TableRow>
                  )}
                  {diag.transactions.map((t) => (
                    <TableRow key={t.id} data-testid={`diag-row-${t.id}`} className={t.suspected_duplicate ? "bg-[#D97706]/10" : ""}>
                      <TableCell className="text-sm tabular-nums whitespace-nowrap">{fmtDate(t.date)}</TableCell>
                      <TableCell className="text-sm max-w-[280px] truncate">
                        {t.counterparty || "—"}
                        {t.suspected_duplicate && <Badge className="ml-2 bg-[#D97706]/15 text-[#D97706] hover:bg-[#D97706]/15 rounded-sm text-[10px]">mogući duplikat</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-[#475569]">{t.category}</TableCell>
                      <TableCell className="text-xs text-[#475569]">{t.source === "uvoz" ? "izvod" : t.source === "korekcija" ? "korekcija" : (t.created_by || "").split("@")[0]}</TableCell>
                      <TableCell className={`text-right text-sm tabular-nums font-medium ${t.type === "naplata" ? "text-[#2F7D4F]" : "text-[#DC2626]"}`}>
                        {t.type === "naplata" ? "+" : "−"}{fmtNum(t.amount)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">{fmtNum(t.running_balance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-[#CBD5E1]">
        <CardHeader className="pb-3"><CardTitle className="text-base">Kontrolne točke</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Banka</TableHead>
                <TableHead>Valuta</TableHead>
                <TableHead className="text-right">Izvod</TableHead>
                <TableHead className="text-right">Aplikacija</TableHead>
                <TableHead className="text-right">Razlika</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Potvrdio</TableHead>
                <TableHead>Napomena</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checkpoints.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-sm text-[#64748B] py-6">Još nema kontrolnih točaka. Kad se stanje slaže, spremite točku — sljedeći put razliku tražite samo od nje nadalje.</TableCell></TableRow>
              )}
              {checkpoints.map((c) => (
                <TableRow key={c.id} data-testid={`checkpoint-row-${c.id}`}>
                  <TableCell className="text-sm tabular-nums">{fmtDate(c.date)}</TableCell>
                  <TableCell className="text-sm">{c.bank}</TableCell>
                  <TableCell className="text-sm">{c.currency}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{fmtNum(c.statement_balance)}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums">{fmtNum(c.computed_balance)}</TableCell>
                  <TableCell className={`text-right text-sm tabular-nums font-medium ${Math.abs(c.difference) < 0.005 ? "text-[#2F7D4F]" : "text-[#DC2626]"}`}>{fmtNum(c.difference)}</TableCell>
                  <TableCell>
                    {c.status === "usklađeno"
                      ? <Badge className="bg-[#2F7D4F]/15 text-[#2F7D4F] hover:bg-[#2F7D4F]/15 rounded-sm text-xs">Usklađeno</Badge>
                      : <Badge className="bg-[#DC2626]/15 text-[#DC2626] hover:bg-[#DC2626]/15 rounded-sm text-xs">Razlika</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-[#475569]">{(c.created_by || "").split("@")[0]}</TableCell>
                  <TableCell className="text-xs text-[#475569] max-w-[220px] truncate" title={c.note} data-testid={`checkpoint-note-${c.id}`}>{c.note || "—"}</TableCell>
                  <TableCell>
                    <Button data-testid={`delete-checkpoint-${c.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#64748B] hover:text-[#DC2626]" onClick={() => removeCheckpoint(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Korekcija salda</DialogTitle>
            <DialogDescription>Knjiženje korekcijske transakcije za usklađivanje s izvodom</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Knjiži se korekcijska transakcija od <strong className="tabular-nums">{result ? `${result.difference > 0 ? "+" : ""}${fmtNum(result.difference)} ${currency}` : ""}</strong> na {bankName} s datumom {fmtDate(date)}, kategorija "korekcija salda".
            </p>
            <p className="text-xs text-[#DC2626]">Koristite samo ako uzrok razlike nije moguće pronaći (npr. stara povijest prije početka rada u aplikaciji). Korekcija je vidljiva u Transakcijama i Aktivnosti.</p>
            <div className="space-y-1.5">
              <Label>Napomena (razlog)</Label>
              <Input data-testid="recon-adjust-note" value={adjustNote} onChange={(e) => setAdjustNote(e.target.value)} placeholder="npr. usklađenje početnog stanja" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setAdjustOpen(false)}>Odustani</Button>
              <Button data-testid="recon-adjust-confirm-btn" className="bg-[#DC2626] hover:bg-[#8E4136] rounded-sm" onClick={adjust} disabled={busy}>Knjiži korekciju</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
